import dayjs from 'dayjs'
import duration, { Duration } from 'dayjs/plugin/duration'

dayjs.extend(duration)

// If you're not sure what the duration codes here mean then study this because it's worth
// being able to read ISO8601 durations:
// +------------------------+----------------------+-------------------------------------+
// |     Duration Type      |   ISO 8601 Format    |            Description             |
// +------------------------+----------------------+-------------------------------------+
// | 1 Day                  | P1D                  | Represents 1 day.                  |
// | 2 Weeks                | P2W                  | Represents 2 weeks (14 days).      |
// | 3 Months               | P3M                  | Represents 3 months.               |
// | 1 Year                 | P1Y                  | Represents 1 year.                 |
// | 1 Year and 2 Months    | P1Y2M                | Represents 1 year and 2 months.    |
// | 1 Day and 12 Hours     | P1DT12H              | Represents 1 day and 12 hours.     |
// | 5 Hours and 30 Minutes | PT5H30M              | Represents 5 hours and 30 minutes. |
// | 45 Minutes             | PT45M                | Represents 45 minutes.             |
// | 30 Seconds             | PT30S                | Represents 30 seconds.             |
// | 2 Days, 4 Hours,       | P2DT4H20M            | Represents 2 days, 4 hours, and    |
// | and 20 Minutes         |                      | 20 minutes.                        |
// +------------------------+----------------------+-------------------------------------+
export const LAST_HOUR = dayjs.duration('PT1H')
export const LAST_12_HOURS = dayjs.duration('PT12H')
export const LAST_DAY = dayjs.duration('P1D')
export const LAST_7_DAYS = dayjs.duration('P7D')
export const LAST_14_DAYS = dayjs.duration('P14D')
export const LAST_30_DAYS = dayjs.duration('P1M')
export const LAST_60_DAYS = dayjs.duration('P2M')
export const LAST_90_DAYS = dayjs.duration('P3MD')
export const LAST_6_MONTHS = dayjs.duration('P6M')
export const LAST_1_YEAR = dayjs.duration('P1Y')
export const LAST_2_YEARS = dayjs.duration('P2Y')

type Props = {
    duration: Duration | undefined
    onChange: (duration: Duration) => void
}

const VALID_TIME_PERIODS_DISPLAY = [
    //tODO: add a row number to all items in this list and make sure they are displayed on the same row in the UI
    { duration: LAST_HOUR, display: '1h', caption: 'Last hour', row: 1 },
    {
        duration: LAST_12_HOURS,
        display: '12h',
        caption: 'Last 12 hours',
        row: 1
    },
    { duration: LAST_DAY, display: '1d', caption: 'Last day', row: 1 },
    { duration: LAST_7_DAYS, display: '7d', caption: 'Last 7 days', row: 1 },
    { duration: LAST_14_DAYS, display: '14d', caption: 'Last 14 days', row: 1 },
    { duration: LAST_30_DAYS, display: '1M', caption: 'Last 1 month', row: 2 },
    { duration: LAST_60_DAYS, display: '2M', caption: 'Last 2 months', row: 2 },
    { duration: LAST_90_DAYS, display: '3M', caption: 'Last 3 months', row: 2 },
    { duration: LAST_6_MONTHS, display: '6M', caption: 'Last 6 months', row: 2 },
    { duration: LAST_1_YEAR, display: '1Y', caption: 'Last 1 year', row: 3 },
    { duration: LAST_2_YEARS, display: '2Y', caption: 'Last 2 years', row: 3 }
]

function RelativeTimePeriod(props: Props) {
    const { duration, onChange } = props

    function handleClick(duration: Duration) {
        onChange(duration)
    }

    const rows = [1, 2, 3].map((rowNum) =>
        VALID_TIME_PERIODS_DISPLAY.filter((period) => period.row === rowNum)
    )

    return (
        <div className="text-ink-900">
            {rows.map((rowPeriods, rowIndex) => (
                <div key={rowIndex} className="flex items-center justify-start">
                    {rowPeriods.map((period) => {
                        const isSelected = duration?.toISOString() === period.duration.toISOString()
                        const classes = isSelected
                            ? 'relative font-semibold inline-flex items-center m-1 px-2 py-2 bg-ink-100 border-0 text-sm hover:bg-ink-50 text-ink-800 focus:z-10 focus:outline-none focus:ring-1 focus:ring-arwen-500 focus:border-arwen-500 rounded-md'
                            : 'relative font-medium inline-flex items-center m-1 px-2 py-2 border-0 bg-white text-sm text-ink-800 hover:bg-ink-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-arwen-500 focus:border-arwen-500 rounded-md'

                        return (
                            <button
                                key={period.display}
                                className={classes}
                                onClick={() => handleClick(period.duration)}
                            >
                                {period.display}
                            </button>
                        )
                    })}
                </div>
            ))}
        </div>
    )
}
export default RelativeTimePeriod
